//
// Copyright (c) 2011-2017 by Curalate, Inc.
//

import _ from "js/underscore-mixins";

(function() {
    // This function generates a function that will be used by ngTables to generate the array of "page" objects for
    // the table pagination. It is necessary to set this function as early as possible to prevent ngTableParams from using
    // its default function to generate the pages array when first loading the page
  const generatePagesArrayFunc = function(pageBlocks) {
    return function(currentPage, totalItems, pageSize) {
      const numPages = Math.ceil(totalItems / pageSize);
      const pages = [];
      let blocks;
      let bound;
      let min;
      let max;

            // Add the prev/first buttons
      pages.push({
        type: 'first',
        number: 1,
        active: currentPage !== 1 && numPages > 1,
      });

      pages.push({
        type: 'prev',
        number: Math.max(1, currentPage - 1),
        active: currentPage !== 1 && numPages > 1,
      });

      if (numPages > 1) {
        blocks = pageBlocks;
        bound = Math.floor(blocks / 2);
        min = currentPage - bound;
        max = currentPage + bound;

                // Shift range according to edge cases
        if (min <= 0) {
                    // If our min bound <= 0, shift the range to the right
          const shiftAmount = Math.abs(min) + 1;
          min += shiftAmount;
          max += shiftAmount;
        }

        if (max >= numPages + 1) {
                    // If the max bound is greater than the number of pages, shift the range left
          min = Math.max(1, min - (max - numPages));
          max = numPages;
        }

        for (let k = min; k < max + 1; k++) {
          pages.push({
            type: 'page',
            number: k,
            active: true,
            current: currentPage === k,
          });
        }
      } else {
                // Add the single page as a placeholder
        pages.push({
          type: 'page',
          number: 1,
          active: false,
        });
      }

            // Add the last/next buttons
      pages.push({
        type: 'next',
        number: Math.min(numPages, currentPage + 1),
        active: currentPage < numPages && numPages > 1,
      });

      pages.push({
        type: 'last',
        number: numPages,
        active: currentPage !== numPages && numPages > 1,
      });

      return pages;
    };
  };

    // Set constant default values for ng-table parameters and settings objects
  const PARAMETER_DEFAULTS = {
    page: 1,
    count: 10,
  };
  const SETTINGS_DEFAULTS = {
    total: 0,
  };
  const PAGER_DEFAULTS = {
    numPageBlocks: 3, // The number of links in the pager not counting prev/first and next/last links
  };

    /**
     * ngTableParamsEx
     *
     * This factory is a vendor of souped up ngTableParams objects, which are used by ng-table
     * Included are default parameters for initial page, initial items to display, and wrapper functions for common
     * table operations.
     *
     * ngTableParamsEx wraps the "getData" function with other useful (and override-able) functions which all contribute
     * to creating an object that represent a query string, formatted in the common format we use in our REST APIs
     *
     * NOTE: Currently, ngTableParamsEx favors tables that are server-side paginated, filtered, and sorted.
     */
  angular.module('curalate').factory('ngTableParamsEx', [
    '$q',
    'ngTableParams',
    function(
            $q,
            ngTableParams,
        ) {
      let extendedFuncs = {
        getFilters() { return {}; },
        getSorting(params) {
          const sorting = params.sorting();
          const ret = {};

          if (sorting && !_.isEmpty(sorting)) {
            const sortKey = _.keys(sorting)[0];
            ret.sort = sortKey;
            ret.desc = sorting[sortKey] === 'desc';
          }

          return ret;
        },
        getQueryParams(params) {
          const paging = {
            offset: ((params.page() - 1) * params.count()) || 0,
            limit: params.count() || -1,
          };

          return _.chain(paging)
                        .extend(extendedFuncs.getFilters(params))
                        .extend(extendedFuncs.getSorting(params))
                        .value();
        },
      };

      return function(baseParameters, baseSettings) {
                // Default these to empty objects if they are not present
        baseParameters = baseParameters || {};
        baseSettings = baseSettings || {};

                // Merge the existing object with defaults
        const params = _.defaults(baseParameters, PARAMETER_DEFAULTS);

                // Set the get data callback to a local var so it doesn't get lost
        const getDataCallback = baseSettings.getData;

                // Use custom functions if defined, otherwise fall back to defaults
        extendedFuncs = _.defaults({
          getFilters: baseSettings.getFilters,
          getSorting: baseSettings.getSorting,
          getQueryParams: baseSettings.getQueryParams,
        }, extendedFuncs);

                // Wrap custom getData function in function that generates query params
                // and passes them into the custom getData function
        function getDataFunc($defer, tableParams) {
          const queryParams = extendedFuncs.getQueryParams(tableParams);

          if (getDataCallback) {
            getDataCallback($defer, queryParams, tableParams);
          } else {
            $defer.resolve([]);
          }
        }

                // Construct the ngTableParams settings object
                // Add new functions into settings so they are retrievable by calling ngTableParamsInstance.settings() later
        const settings = _.chain(baseSettings)
                    .extend({
                      getData: getDataFunc,
                    })
                    .defaults(SETTINGS_DEFAULTS)
                    .extend({
                      getFilters: extendedFuncs.getFilters,
                      getSorting: extendedFuncs.getSorting,
                    })
                    .value();

        const ret = new ngTableParams(params, settings);

        ret.generatePagesArray = generatePagesArrayFunc(PAGER_DEFAULTS.numPageBlocks);

        return ret;
      };
    }]);

    /**
     * ngClientSideTableParams
     *
     * Factory that builds upon ngTableParamsEx to provide better support for client-side paginated tables, i.e. tables whose processing logic is handled
     * in client-side Javascript.
     *
     * The factory provides an extended API upon ngTableParamsEx as well as some default functionality for processing data.
     * This object provides preprocess, process, and postprocess functions that data gets threaded through on load, refresh, and event (sort, filter)
     *  * preprocess, by default, is an identity function. This function is meant to be used in order to get data into a form that can be processed (an array)
     *  * process, by default, sorts the data based on current parameters and sets the total length of the table
     *  * postprocess, by default, slices the data given the current offset/limit
     *
     * Additionally, ngClientSideTableParams provides a facility in which data is only retrieved from the server once. An "invalidate" function is also provided
     * in the event the caller wants to hit the server again
     */
  angular.module('curalate').factory('ngClientSideTableParams', [
    '$q',
    'ngTableParamsEx',
    function(
            $q,
            ngTableParamsEx,
        ) {
      const extendedFuncs = {};

            // Default preprocessData function: eseentially a no-op that returns untouched data
      extendedFuncs.preprocess = function(data) { return data; };

            // Default processData function. Takes care of sorting.
            // NOTE: this function expects "data" to be an array, which implies that "preprocessData" should return an array
      extendedFuncs.process = function(data, queryParams, tableParams) {
        let workingCopy = data;
                // TODO support multi-value sort
        if (queryParams.sort) {
          workingCopy = _.sortBy(workingCopy, queryParams.sort);
          if (queryParams.desc) {
            workingCopy.reverse();
          }
        }

        tableParams.total(data.length);

        return workingCopy;
      };

            // Default postprocessData function: slices data for current offset/limit
      extendedFuncs.postprocess = function(data, queryParams) {
        return data.slice(queryParams.offset, queryParams.offset + queryParams.limit);
      };

      return function(baseParameter, baseSettings) {
        let cachedData;
        let gotData = false;
        const invalidate = function() {
          cachedData = [];
          gotData = false;
        };
        const getDataCallback = baseSettings.getData;

        function getDataFunc($defer, queryParams, tableParams) {
                    // Create a new deferred object that sits in front of the one supplied from ngTable
                    // This deferred is used for passing returned data in its raw state for caching and processing
          const doneDeferred = $q.defer();

          doneDeferred.promise.then((data) => {
            const preprocessedData = tableParams.settings().preprocess(data, queryParams, tableParams);

            if (!gotData && tableParams.settings().shouldCache) {
                            // Call preprocess only once
              cachedData = preprocessedData;
            }

            const processedData = tableParams.settings().processData(preprocessedData, queryParams, tableParams);
            $defer.resolve(processedData);
          });

                    // Indicate that the data has been loaded
          $defer.promise.then(() => {
            if (!gotData) {
              gotData = true;
            }
          });

          if (getDataCallback) {
            if (!gotData || !tableParams.settings().shouldCache) {
              getDataCallback(doneDeferred, tableParams);
            } else {
              doneDeferred.resolve(cachedData);
            }
          } else if (baseSettings.data) {
            doneDeferred.resolve(baseSettings.data);
          } else {
            $defer.resolve([]);
          }
        }

        const settings = _.defaults(baseSettings, extendedFuncs);

                // Create the final process function
                // postProcess(process(preProcess(data)))
        function processFunc(data, queryParams, tableParams) {
          const p2 = _.partial(settings.process, _, queryParams, tableParams);
          const p3 = _.partial(settings.postprocess, _, queryParams, tableParams);

          return _.compose(p3, p2)(data);
        }

        const extendedSettings = _.extend(settings, {
          getData: getDataFunc,
          processData: processFunc,
          gotData,
          invalidate,
        });

        return new ngTableParamsEx(baseParameter, extendedSettings);
      };
    }]);
})();
